import React, { useEffect, useState, useRef } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Link } from "react-router-dom";

const SkeletonLoader = () => (
  <div className="flex gap-8 md:gap-12 lg:gap-16 items-center w-full h-full px-8 md:px-16">
    {[...Array(9)].map((_, index) => (
      <div key={index} className="flex flex-col items-center justify-between gap-4 animate-pulse flex-shrink-0">
        <div className="h-24 w-24 md:h-32 md:w-32 lg:h-36 lg:w-36 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full shadow-lg border-2 border-gray-200"></div>
        <div className="h-4 w-20 md:w-24 bg-gray-200 rounded-full"></div>
      </div>
    ))}
  </div>
);

export default function Make() {
  const [makes, setMakes] = useState([]);
  const scrollRef = useRef(null);
  const [isAppending, setIsAppending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const itemWidth = 100;
  const itemWidthMd = 120;

  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const response = await fetch('/api/listing/make');
        if (!response.ok) {
          console.error('API error:', response.status);
          setMakes([]);
          setLoading(false);
          return;
        }
        const data = await response.json();
        // API now returns objects with make and image
        const makesArray = Array.isArray(data) ? data.map(item => ({
          make: item.make || item,
          image: item.image || null
        })) : [];
        setMakes(makesArray);
        setLoading(false);
      } catch (error) {
        setMakes([]);
        setLoading(false);
      }
    };

    fetchMakes();
  }, []);

  const checkScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = window.innerWidth < 768 ? itemWidth + 24 : itemWidthMd + 48;
      if (direction === "left") {
        scrollRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      } else {
        scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
      setTimeout(checkScrollButtons, 300);
    }
  };

  const handleInfiniteScroll = () => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;
    
    const nearEnd = scrollElement.scrollLeft + scrollElement.offsetWidth >= scrollElement.scrollWidth - 200;
    checkScrollButtons();

    if (nearEnd && !isAppending) {
      setIsAppending(true);
      setTimeout(() => {
        setMakes((prevMakes) => [...prevMakes, ...prevMakes]);
        setIsAppending(false);
      }, 500);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      checkScrollButtons();
      scrollRef.current.addEventListener("scroll", handleInfiniteScroll);
      window.addEventListener("resize", checkScrollButtons);
    }

    return () => {
      if (scrollRef.current) {
        scrollRef.current.removeEventListener("scroll", handleInfiniteScroll);
      }
      window.removeEventListener("resize", checkScrollButtons);
    };
  }, [makes]);

  return (
    <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 group">
      {/* Navigation Buttons */}
      {canScrollLeft && (
        <button
          onClick={() => scroll("left")}
          className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-40 bg-white hover:bg-gray-50 p-3 md:p-4 rounded-full shadow-xl border border-gray-200 hover:border-[#DC2626] transition-all duration-300 hover:scale-110 active:scale-95"
          aria-label="Scroll left"
        >
          <FaChevronLeft className="text-gray-700 hover:text-[#DC2626] transition-colors text-sm md:text-base" />
        </button>
      )}

      {canScrollRight && (
        <button
          onClick={() => scroll("right")}
          className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-40 bg-white hover:bg-gray-50 p-3 md:p-4 rounded-full shadow-xl border border-gray-200 hover:border-[#DC2626] transition-all duration-300 hover:scale-110 active:scale-95"
          aria-label="Scroll right"
        >
          <FaChevronRight className="text-gray-700 hover:text-[#DC2626] transition-colors text-sm md:text-base" />
        </button>
      )}

      {/* Carousel Container */}
      <div
        ref={scrollRef}
        className="h-40 md:h-56 lg:h-64 bg-gradient-to-br from-white via-gray-50 to-white rounded-3xl shadow-xl border-2 border-gray-100 overflow-x-auto no-scrollbar relative z-20 cursor-grab active:cursor-grabbing backdrop-blur-sm"
      >
        {loading ? (
          <SkeletonLoader />
        ) : (
          <div className="flex justify-start gap-8 md:gap-12 lg:gap-16 items-center w-max h-full px-8 md:px-16 py-6">
            {makes.map((makeItem, index) => {
              const makeName = typeof makeItem === 'string' ? makeItem : (makeItem.make || makeItem);
              const makeImage = typeof makeItem === 'object' ? makeItem.image : null;
              return (
                <Link 
                  key={`${makeName}-${index}`} 
                  to={`/cars/make/${makeName}`}
                  className="flex flex-col items-center justify-between gap-4 flex-shrink-0 group/item transition-all duration-500 hover:scale-110 active:scale-100"
                >
                  {/* Logo Container */}
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#DC2626]/20 to-transparent opacity-0 group-hover/item:opacity-100 blur-xl transition-opacity duration-500 -z-10 scale-125"></div>
                    
                    <div className="relative h-24 w-24 md:h-32 md:w-32 lg:h-36 lg:w-36 bg-gradient-to-br from-white to-gray-50 rounded-full p-3 md:p-4 lg:p-5 shadow-lg border-2 border-gray-200 group-hover/item:border-[#DC2626] group-hover/item:shadow-2xl group-hover/item:shadow-[#DC2626]/20 transition-all duration-500 flex items-center justify-center overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#DC2626]/5 to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity duration-500 rounded-full"></div>
                      
                      <div className="relative w-full h-full rounded-full overflow-hidden flex items-center justify-center">
                        {makeImage ? (
                          <img
                            src={makeImage}
                            alt={`${makeName} logo`}
                            className="w-full h-full rounded-full object-cover transition-all duration-500 group-hover/item:scale-110 group-hover/item:brightness-110 filter drop-shadow-sm group-hover/item:drop-shadow-md"
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<div class="w-full h-full rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-500 font-semibold text-xs">${makeName.charAt(0)}</div>`;
                            }}
                          />
                        ) : (
                          <div className="w-full h-full rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-500 font-semibold text-xs">
                            {makeName.charAt(0)}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-full opacity-0 group-hover/item:opacity-100 transition-all duration-500 shadow-lg shadow-[#DC2626]/50 animate-pulse"></div>
                    
                    <div className="absolute inset-0 rounded-full border-2 border-[#DC2626] opacity-0 group-hover/item:opacity-30 group-hover/item:scale-125 transition-all duration-700"></div>
                  </div>
                  
                  {/* Brand Name */}
                  <div className="text-center">
                    <span className="text-sm md:text-base lg:text-lg font-semibold text-gray-800 group-hover/item:text-[#DC2626] transition-all duration-300 text-center min-w-[80px] md:min-w-[100px] block">
                      {makeName}
                    </span>
                    <div className="mt-1 h-0.5 w-0 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] mx-auto group-hover/item:w-full transition-all duration-500 rounded-full"></div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
